import { fichiersDeJeuSchema } from "./fichiers-jeu.js";
import { sansPorteeInterdite } from "./portee-jeu.mjs";
import { z } from "zod";

import { baseEntityFields } from "./base.js";
import { FAMILLE_PERSONNES } from "./personne.js";
import { fautesDeDependance } from "./choix-dependants.js";
import { couleurSchema } from "./couleurs.js";
import { accepteReglage, estAutomatique, IDS_TYPES_DE_CHAMP, sansTypeFondu, type ReglageDeChamp } from "./catalogue-des-types.mjs";
import { analyserFormule, champsAvecNatifs, LONGUEUR_MAX_FORMULE, verifierFormule } from "./formule.js";
import { modeleDeNumeroValide } from "./types-de-champ.js";
import { conditionSchema } from "./conditions.js";

/**
 * Types primitifs supportés par un champ personnalisé (customField).
 * Convention modélisée dès maintenant (cf. bible v2) : chaque org peut définir
 * des champs additionnels sur ses entités sans forker le cœur.
 */
export const customFieldTypeSchema = z.enum([
  "text",
  "textarea",
  "number",
  "boolean",
  "date",
  "datetime",
  "select",
  "multiselect",
  "email",
  "phone",
  "url",
  /**
   * L'ID D'UNE FICHE D'UNE AUTRE FAMILLE. La valeur stockée est une chaîne (l'id
   * du document visé) ; ce que le champ AJOUTE à un `text`, c'est de dire de
   * quelle famille cette fiche vient (`reference`, ci-dessous), pour que l'écran
   * en affiche le NOM et l'ouvre, sans qu'une porte du même écran ait à la
   * montrer. C'est le seul type qui désigne autre chose que sa
   * propre valeur.
   */
  "lien",
  /**
   * UNE PERSONNE DE L'ORG. La valeur stockée est un `personId` (annuaire
   * `personnes`, `personne.ts`), jamais un e-mail : l'adresse change, la
   * personne reste. C'est un `lien` dont la famille est
   * connue d'avance : l'écran en affiche le NOM et propose un sélecteur des
   * personnes de l'org, sans case de texte libre.
   */
  "personne",
  /*
   * LES TYPES DU 27/10/2026 (`docs/socle/champs-types.md`). Aucun n'invente une
   * forme de valeur : chacun stocke ce que le sac accepte deja. Ce que chacun
   * EST (libelle, nature, reglages) vit dans le catalogue
   * (`catalogue-des-types.mjs`), qu'un test aligne sur cette liste. Aucun ne
   * double un type existant : le montant, le pourcentage, la duree et les liens
   * multiples sont des capacites de `number` et de `lien` (leurs reglages).
   */
  /** DES ETIQUETTES LIBRES : une liste de chaines creees en tapant, sans catalogue d'options (la nature « liste de textes » du Studio). */
  "etiquettes",
  /** UNE PERIODE : la chaine de deux dates pures separees par une barre oblique (ISO 8601). */
  "periode",
  /** UNE LISTE DE CONTROLE : une liste de chaines, chacune une case cochee ou non puis son texte. */
  "checklist",
  /** UNE NOTE : un entier de 0 a `max` (5 par defaut), montre en etoiles. */
  "note",
  /** UN CALCUL : un nombre tire des autres champs par sa `formule` (`formule.ts`), recalcule par le socle. */
  "calcul",
  /** UN NUMERO AUTOMATIQUE : la chaine deja formatee par sa `sequence`, attribuee par le serveur. */
  "numero",
  /** UNE LECTURE : un champ de la fiche designee par un lien, montre en lecture seule, jamais stocke (`docs/socle/champ-lie-lecture.md`). */
  "lecture",
]);

/** Type d'un champ personnalisé. */
export type CustomFieldType = z.infer<typeof customFieldTypeSchema>;

/**
 * UN NOM DE CHAMP DANS LE VOCABULAIRE D'UNE AUTRE FAMILLE : `socle:name` pour
 * un champ que la famille visée porte nativement, `libelle` pour une clef du sac
 * `customFields` de ses documents. La même forme que dans `taki.screens.json`,
 * et pour la même raison : c'est la seule façon de dire lequel des deux accès
 * lire sur une fiche dont on ne connaît pas la déclaration.
 */
const nomDeChampVise = z
  .string()
  .regex(
    /^(?:socle:)?[a-z][a-zA-Z0-9_]*$/,
    "un nom de champ est une clef déclarée, ou socle:<champ natif>",
  );

/**
 * CE QU'UN CHAMP `lien` DÉSIGNE : la FAMILLE visée (au pluriel du SDK, comme
 * `jeu.family`), son sous-type quand la famille en spécialise, et de quoi lire
 * le TITRE d'une de ses fiches.
 *
 * POURQUOI LA FAMILLE ET PAS UNE PORTE. Une porte appartient à UN écran ; une
 * référence traverse les apps (le client d'un projet Timesheet est un contact de
 * l'app Contact). Nommer la famille est la seule adresse que toutes les apps
 * partagent : c'est celle du SDK, et celle des rules.
 *
 * `titre` / `sousTitre` SONT FACULTATIFS, et le socle a une convention pour les
 * familles qui portent un nom (`titreDeFiche`, `familles.ts`). Ils se déclarent
 * quand la famille visée n'en porte pas (un `project` se nomme par un champ
 * sur-mesure de l'app qui le gère : `titre: "libelle"`), ou quand l'app veut un
 * autre mot que celui de la convention.
 *
 * `.strict()` : une clef inconnue ici est une faute, jamais une extension
 * silencieuse. La forme est celle de `ColumnReference` (`@taki/ui`), moins les
 * fonctions : ce fichier est de la DONNÉE.
 */
export const fieldReferenceSchema = z
  .object({
    family: z.string().min(1),
    type: z.string().min(1).optional(),
    titre: nomDeChampVise.optional(),
    sousTitre: nomDeChampVise.optional(),
    /**
     * LES FICHES QUE LE SELECTEUR PROPOSE : une condition du socle (la meme
     * que la tranche d'une porte, `conditions.ts`), lue DANS LE VOCABULAIRE
     * DE LA FAMILLE VISEE, comme `titre`. Le « Produit propose » d'une
     * proposition ne propose que l'offre disponible :
     * `{ et: [{ champ: "sens", op: "parmi", valeurs: ["offre"] }, ...] }`.
     *
     * ELLE FILTRE LE CHOIX, JAMAIS LA DONNEE : une valeur deja posee hors
     * condition reste valide, s'affiche, et le selecteur la garde en tete en
     * le disant. Absente : tout le jeu. Detail : `docs/socle/lien-choix-filtre.md`.
     */
    choix: conditionSchema.optional(),
  })
  .strict();

/** La cible d'un champ `lien`. */
export type FieldReference = z.infer<typeof fieldReferenceSchema>;

/**
 * LA RÈGLE `lien` <=> `reference`, ÉCRITE UNE FOIS : un champ `lien` sans cible
 * ne dit pas quoi nommer, une cible sur un autre type est une clef morte. Les
 * trois schémas qui portent un `type` de champ (définition persistée, champ
 * déclaré, colonne native d'écran) l'appellent tels quels, pour que la faute
 * ait le même mot partout.
 */
export function verifierReferenceDeChamp(
  champ: { type?: string | undefined; reference?: unknown },
  quoi: string,
  ctx: z.RefinementCtx,
): void {
  const visant = champ.type === "lien";
  if (visant && champ.reference == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${quoi} est de type ${champ.type} : il lui faut une référence { family, type?, titre? }`,
    });
  }
  /*
   * UN CHAMP `personne` VISE TOUJOURS L'ANNUAIRE : sa cible est implicite, et
   * il n'en déclare une que pour choisir le mot affiché (`titre:
   * "socle:email"`). Une autre famille ferait d'une personne un lien déguisé.
   */
  if (champ.type === "personne") {
    const famille = (champ.reference as { family?: unknown } | null | undefined)?.family;
    if (champ.reference != null && famille !== FAMILLE_PERSONNES) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${quoi} est de type personne : un champ personne ne vise que la famille ${FAMILLE_PERSONNES}`,
      });
    }
    return;
  }
  if (!visant && champ.reference != null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${quoi} porte une référence : seul un champ de type lien (ou personne) en prend une`,
    });
  }
}

/* -------------------------------------------------------------------------- */
/* Les reglages d'un type                                                     */
/* -------------------------------------------------------------------------- */

/**
 * LA SEQUENCE D'UN NUMERO AUTOMATIQUE : le modele du numero formate, et le
 * nombre de chiffres auquel le numero se complete de zeros. Le modele contient
 * `{numero}` (ou `{numéro}`) et peut contenir `{annee}` (`{année}`) :
 * `ASS-{année}-{numéro}` avec `chiffres: 4` donne « ASS-2026-0042 ».
 *
 * FACULTATIVE sur le champ : sans elle, le numero nu (`SEQUENCE_PAR_DEFAUT`,
 * `types-de-champ.ts`). L'ETAT (le prochain numero) n'est pas ici : il vit dans
 * `orgs/{orgId}/sequences/`, c'est un etat, pas une declaration.
 */
export const sequenceDeNumeroSchema = z
  .object({
    modele: z
      .string()
      .min(1)
      .max(60)
      .refine(modeleDeNumeroValide, "le modèle d'un numéro contient {numero} (ou {numéro}), la place du numéro"),
    chiffres: z.number().int().min(1).max(12).optional(),
  })
  .strict();

/**
 * LE REGLAGE D'UNE LECTURE : le chemin de liens a suivre depuis la fiche (`via`,
 * un ou deux champs `lien`, le premier de CE jeu, le second du jeu que le
 * premier designe) et le champ LU sur la derniere fiche atteinte (`champ`, dans
 * SON vocabulaire : une clef du sac, ou `socle:<natif>`). Le nombre d'etages se
 * juge dans `verifierReglagesDeChamp`, qui nomme le champ ; les champs lies se
 * jugent contre le fichier (`fautesDeLecture`). Rien ne se stocke.
 */
export const lectureDeChampSchema = z
  .object({
    via: z.array(nomDeChampVise),
    champ: nomDeChampVise,
  })
  .strict();

/** Le reglage d'une lecture. */
export type LectureDeChamp = z.infer<typeof lectureDeChampSchema>;

/** Le nombre d'etages d'une lecture : « et au besoin un etage de plus ». */
export const ETAGES_MAX_D_UNE_LECTURE = 2;

/**
 * LES REGLAGES D'UN TYPE, partages par le champ declare et la definition
 * persistee (meme raison que le reste de leurs briques). Chacun ne vaut que
 * pour les types que le catalogue nomme (`reglages`, `catalogue-des-types.mjs`) ;
 * pose sur un autre, il est refuse (`verifierReglagesDeChamp`). Tous sont
 * facultatifs, sauf la formule d'un calcul.
 */
const reglagesDeChamp = {
  /** L'UNITE affichee apres le nombre (« m² », « kg ») : un reglage d'affichage, la valeur reste le nombre nu. `number`, `calcul`. */
  unite: z.string().trim().min(1).max(12).optional(),
  /**
   * LA DEVISE, code ISO 4217 : un `number` ou un `calcul` qui la porte est un
   * MONTANT (format monetaire, centimes, somme au centime, `formatDuNombre`).
   * `number`, `calcul`.
   */
  devise: z.string().regex(/^[A-Z]{3}$/, "une devise est un code ISO 4217 en trois majuscules (EUR, USD, CHF)").optional(),
  /** LE NOMBRE DE DECIMALES montrees (et, pour un calcul, gardees). `number`, `calcul`. */
  decimales: z.number().int().min(0).max(6).optional(),
  /**
   * L'AFFICHAGE d'un nombre : `jauge`, la barre d'un POURCENTAGE de 0 a 100,
   * reglable d'un clic. Un affichage seulement : la valeur stockee reste le
   * nombre nu. `number`.
   */
  affichage: z.enum(["jauge"]).optional(),
  /**
   * PLUSIEURS FICHES : un `lien` qui le porte stocke une LISTE d'ids de fiches
   * de sa `reference`, au lieu d'un id. Seul `true` s'ecrit (l'absence est le
   * lien simple, celui de toujours). `lien`.
   */
  plusieurs: z.literal(true).optional(),
  /** LA NOTE MAXIMALE, de 3 a 10 (5 par defaut). `note`. */
  max: z.number().int().min(3).max(10).optional(),
  /**
   * LA FORMULE d'un calcul, en forme stockee (`{prix} * 0.25`) : la seule
   * expression du fichier, dans un langage ferme (`formule.ts`). Exigee par
   * `calcul`, refusee partout ailleurs.
   */
  formule: z.string().trim().min(1).max(LONGUEUR_MAX_FORMULE).optional(),
  /** LA SEQUENCE d'un numero automatique ({@link sequenceDeNumeroSchema}). `numero`. */
  sequence: sequenceDeNumeroSchema.optional(),
  /** LA LECTURE d'un champ lu sur la fiche liee ({@link lectureDeChampSchema}). Exigee par `lecture`, refusee partout ailleurs. */
  lecture: lectureDeChampSchema.optional(),
};

/** Les reglages que `verifierReglagesDeChamp` juge par le catalogue (les plus anciens ont leurs regles a eux). */
const REGLAGES_DU_CATALOGUE: readonly ReglageDeChamp[] = ["unite", "devise", "decimales", "affichage", "plusieurs", "max", "formule", "sequence", "lecture"];

/** Le reglage dans une phrase de refus : son nom, sauf ceux dont le nom seul ne se lit pas. */
const MOT_DU_REGLAGE: Partial<Record<ReglageDeChamp, string>> = {
  affichage: "d'affichage en jauge",
  plusieurs: "de liens multiples (plusieurs)",
};

/** Les types qui acceptent ce reglage, en clair : « number ou calcul ». */
const typesQuiAcceptent = (reglage: ReglageDeChamp): string =>
  IDS_TYPES_DE_CHAMP.filter((type) => accepteReglage(type, reglage)).join(" ou ");

/**
 * LA REGLE « UN REGLAGE VA AVEC SON TYPE », ecrite une fois pour le champ
 * declare et la definition persistee, avec le mot de `verifierReferenceDeChamp`.
 * Un reglage sur un autre type est une clef morte qui ment sur le champ ; un
 * calcul sans formule ne calcule rien. La formule n'est jugee ici que sur sa
 * LECTURE : ses champs cites se jugent contre le jeu (`appFieldsFileSchema`).
 */
export function verifierReglagesDeChamp(
  champ: { type?: string | undefined; required?: boolean | undefined } & Partial<Record<ReglageDeChamp, unknown>>,
  quoi: string,
  ctx: z.RefinementCtx,
): void {
  /* UN CALCUL, UN NUMERO OU UNE LECTURE N'EST JAMAIS OBLIGATOIRE : sa valeur est
     posee par le socle, personne ne peut la saisir. Obligatoire, il bloquait la creation
     (« manque Numero ») sur une case que la fiche neuve n'offre pas. */
  if (champ.required === true && estAutomatique(champ.type)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${quoi} est de type ${champ.type} : sa valeur est posée par le socle, il ne peut pas être obligatoire`,
      path: ["required"],
    });
  }
  for (const reglage of REGLAGES_DU_CATALOGUE) {
    if (champ[reglage] === undefined || accepteReglage(champ.type, reglage)) continue;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${quoi} est de type ${champ.type} : il ne prend pas ${MOT_DU_REGLAGE[reglage] ?? `de ${reglage}`} (réservé à ${typesQuiAcceptent(reglage)})`,
      path: [reglage],
    });
  }
  /* UN MONTANT N'A PAS D'UNITE : la devise en tient lieu. Une jauge montre un
     pourcentage : ni devise, ni unite. */
  if ((champ.type === "number" || champ.type === "calcul") && champ.devise !== undefined && champ.unite !== undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${quoi} est de type ${champ.type} : une devise ou une unité, pas les deux`,
      path: ["unite"],
    });
  }
  if (champ.type === "number" && champ.affichage === "jauge" && (champ.devise !== undefined || champ.unite !== undefined)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${quoi} s'affiche en jauge, un pourcentage de 0 à 100 : ni devise ni unité`,
      path: [champ.devise !== undefined ? "devise" : "unite"],
    });
  }
  if (champ.type === "lecture") {
    const lecture = champ.lecture as LectureDeChamp | undefined;
    if (lecture === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${quoi} est de type lecture : il lui faut un réglage lecture { via, champ }`,
        path: ["lecture"],
      });
    } else if (lecture.via.length === 0 || lecture.via.length > ETAGES_MAX_D_UNE_LECTURE) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${quoi} est de type lecture : via nomme un ou deux champs lien (${lecture.via.length === 0 ? "aucun" : lecture.via.length} donné${lecture.via.length > 1 ? "s" : ""}), pas plus de ${ETAGES_MAX_D_UNE_LECTURE} étages`,
        path: ["lecture", "via"],
      });
    }
    return;
  }
  if (champ.type !== "calcul") return;
  if (typeof champ.formule !== "string") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${quoi} est de type calcul : il lui faut une formule`,
      path: ["formule"],
    });
  } else {
    const lue = analyserFormule(champ.formule);
    if ("erreur" in lue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${quoi} : la formule est illisible, ${lue.erreur} (caractère ${lue.position + 1})`,
        path: ["formule"],
      });
    }
  }
}

/**
 * Option d'un champ de type `select` / `multiselect`.
 * - `value` : valeur stockée (stable, machine).
 * - `label` : libellé affiché (humain).
 */
export const customFieldOptionSchema = z.object({
  archived: z.boolean().optional(),
  value: z.string().min(1),
  label: z.string().min(1),
  /**
   * Couleur d'affichage, le SEUL vocabulaire de couleur partagé entre apps :
   * une clef de la grille du design (les quatre tons d'avant en tête, qui
   * suivent le thème) ou un hex gardé tel quel (`couleurs.ts`). Absente = neutre.
   */
  tone: couleurSchema.optional(),
  /**
   * LA VALEUR DU CHAMP PARENT a laquelle cette option appartient, quand son
   * champ declare `dependDe` (le job « Devis » appartient au pole `sales`).
   * Absente : l'option vaut pour tous les parents. Detail :
   * `choix-dependants.ts`.
   */
  parent: z.string().min(1).optional(),
});

/** Type d'une option de champ select. */
export type CustomFieldOption = z.infer<typeof customFieldOptionSchema>;

/**
 * Définition d'un champ personnalisé (`customFieldDefinition`).
 *
 * Décrit UN champ custom rattaché à une entité (`entity`) pour une org donnée.
 * Étend les champs standard car une définition est elle-même un document Firestore.
 *
 * - `orgId`    : org propriétaire de la définition.
 * - `entity`   : nom de l'entité cible (ex. "contact").
 * - `key`      : clé technique du champ (stable, stockée sur les documents cibles).
 * - `label`    : libellé affiché.
 * - `type`     : type primitif du champ.
 * - `required` : champ obligatoire à la saisie.
 * - `options`  : options pour les types select/multiselect.
 * - `order`    : ordre d'affichage dans les formulaires.
 * - `helpText` : texte d'aide optionnel.
 */
export const customFieldDefinitionSchema = z.object({
  ...baseEntityFields,
  orgId: z.string(),
  entity: z.string().min(1),
  key: z
    .string()
    .min(1)
    .regex(
      /^[a-z][a-zA-Z0-9_]*$/,
      "key doit commencer par une minuscule et ne contenir que [a-zA-Z0-9_]",
    ),
  label: z.string().min(1),
  type: customFieldTypeSchema,
  required: z.boolean().default(false),
  options: z.array(customFieldOptionSchema).optional(),
  /** La cible d'un champ `lien`, et rien pour les autres types (`plusieurs` : une liste d'ids). */
  reference: fieldReferenceSchema.optional(),
  /** La clef du champ a choix dont les options de celui-ci dependent. */
  dependDe: z.string().min(1).optional(),
  order: z.number().int().nonnegative().default(0),
  helpText: z.string().optional(),
  ...reglagesDeChamp,
}).superRefine((champ, ctx) => {
  verifierReferenceDeChamp(champ, `le champ "${champ.key}"`, ctx);
  verifierReglagesDeChamp(champ, `le champ "${champ.key}"`, ctx);
});

/** Type d'une définition de champ personnalisé. */
export type CustomFieldDefinition = z.infer<typeof customFieldDefinitionSchema>;

/* -------------------------------------------------------------------------- */
/* Le fichier de champs d'une app : `taki.fields.json`                         */
/* -------------------------------------------------------------------------- */

/**
 * UN CHAMP DÉCLARÉ par une app, dans son `taki.fields.json`.
 *
 * C'est le MÊME objet que {@link customFieldDefinitionSchema}, moins ce qui
 * appartient à un document Firestore (`orgId`, les champs standard, `order` que
 * l'ordre du tableau porte déjà). Les deux partagent volontairement leurs
 * briques : le jour où un client redéfinit ses champs dans SON environnement,
 * le fichier et le document parlent déjà la même langue, et la migration est une
 * copie, pas une traduction.
 *
 * CE QU'IL NE PORTE PAS, ET C'EST LA LIGNE ROUGE DU CHANTIER : aucune closure,
 * aucun `render`, aucun `format`, aucune expression. Ce fichier est de la
 * DONNÉE que le code importe, jamais un programme qu'un moteur interprète. La
 * première clé qui décrit un calcul est un langage de gabarit qui commence, et
 * un langage maison dégrade exactement ce que les agents savent le mieux faire :
 * du React et du TypeScript ordinaires.
 *
 * UNE SEULE EXCEPTION, decidee par le fondateur le 27/10/2026 : la `formule`
 * d'un champ `calcul`. Elle tient parce qu'elle n'est pas un programme : un
 * langage FERME (nombres, champs du jeu, quatre operations, puissance, MIN,
 * MAX, ABS, ARRONDI), lu par un analyseur sans `eval` et partage tel quel par
 * le client et le serveur (`formule.ts`). Aucune autre clef de calcul, de
 * condition ou de gabarit ne s'ajoute sur ce modele. La condition des CHOIX
 * d'un lien (`reference.choix`) n'en est pas une : c'est l'arbre de donnee
 * deja lu par les tranches et les filtres d'ecran, pas un langage de plus.
 */
export const appFieldSchema = z
  .object({
    /**
     * La clé technique, stockée sur chaque document. IMMUABLE une fois posée :
     * la renommer laisserait les documents existants sur l'ancienne clé, ce qui
     * est une migration de données, pas une modification de champ.
     */
    key: z
      .string()
      .min(1)
      .regex(
        /^[a-z][a-zA-Z0-9_]*$/,
        "key doit commencer par une minuscule et ne contenir que [a-zA-Z0-9_]",
      ),
    label: z.string().min(1),
    type: customFieldTypeSchema,
    required: z.boolean().optional(),
    options: z.array(customFieldOptionSchema).optional(),
    helpText: z.string().optional(),
    /**
     * L'EXEMPLE GRIS de la case vide, dans la fiche de CREATION et dans
     * l'editeur (« Sony », « PXW-FX9 », « 290 »). C'est ce qui remplace
     * « Non renseigne » sur une fiche qui n'existe pas encore : rien n'a ete
     * renseigne, tout est a ecrire, et le mot doit montrer la forme attendue.
     *
     * Facultatif, et `min(1)` : le socle a un placeholder par defaut par type
     * d'editeur (`placeholderParDefaut`, @taki/ui). Une chaine vide declaree
     * n'effacerait pas ce defaut, elle ne dirait rien du tout.
     */
    placeholder: z.string().min(1).optional(),
    /**
     * LA CIBLE D'UN CHAMP `lien` : la famille dont ce champ porte l'id d'une
     * fiche, et de quoi la nommer. Exigée par ce type, refusée sur tous les
     * autres (`verifierReferenceDeChamp`), comme `options` avec `select`.
     */
    reference: fieldReferenceSchema.optional(),
    /**
     * LES CHOIX DEPENDENT D'UN AUTRE CHAMP DU JEU : la clef d'un `select` du
     * meme jeu, dont chaque option de celui-ci nomme la valeur (`parent`). Le
     * Job ne propose que les jobs du Pole de la fiche. Detail et limites :
     * `choix-dependants.ts`.
     */
    dependDe: z
      .string()
      .regex(/^[a-z][a-zA-Z0-9_]*$/, "dependDe nomme la clef d'un champ du meme jeu")
      .optional(),
    /**
     * LES REGLAGES D'UN TYPE (`unite`, `devise`, `decimales`, `affichage`,
     * `plusieurs`, `max`, `formule`, `sequence`), chacun reserve aux types que
     * le catalogue nomme (`verifierReglagesDeChamp`).
     */
    ...reglagesDeChamp,
  })
  .superRefine((champ, ctx) => {
    /*
     * Un catalogue sans type de choix est une clé morte ; un type de choix sans
     * catalogue est un sélecteur vide. Les deux se voient à la lecture, aucun
     * ne se voit à la compilation : l'import JSON élargit tout en `string`.
     */
    const aBesoin = champ.type === "select" || champ.type === "multiselect";
    if (aBesoin && (champ.options == null || champ.options.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `le champ "${champ.key}" est de type ${champ.type} : il lui faut des options`,
      });
    }
    if (!aBesoin && champ.options != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `le champ "${champ.key}" est de type ${champ.type} : il ne prend pas d'options`,
      });
    }
    /* Même logique pour la cible d'un lien : elle va avec son type, et avec lui seul. */
    verifierReferenceDeChamp(champ, `le champ "${champ.key}"`, ctx);
    /* Et pour chaque reglage d'un type (unite, devise, formule...). */
    verifierReglagesDeChamp(champ, `le champ "${champ.key}"`, ctx);
    if (champ.dependDe !== undefined && !aBesoin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `le champ "${champ.key}" est de type ${champ.type} : seul un champ a choix depend d'un autre`,
      });
    }
    if (champ.dependDe === undefined && (champ.options ?? []).some((o) => o.parent !== undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `le champ "${champ.key}" range ses options sous un parent : il lui faut dependDe`,
      });
    }
  });

/** Un champ déclaré par une app. */
export type AppField = z.infer<typeof appFieldSchema>;

/* -------------------------------------------------------------------------- */
/* LA FAMILLE PAS ENCORE DÉCIDÉE                                              */
/* -------------------------------------------------------------------------- */

/**
 * LE TROU NOMMÉ : la famille d'un jeu que personne n'a encore tranchée.
 *
 * POURQUOI IL EXISTE. Une app naît avec ses DEUX fichiers de déclaration, sans
 * condition. Sans ça, déléguer la famille à l'agent produisait une app SANS
 * `taki.fields.json`, donc une app que le Studio ne pouvait pas modifier : ni
 * champ à éditer, ni « Ajouter un champ », ni porte renommable. L'inverse exact
 * de ce que le chantier promet. Un fichier absent verrouille tout ; un trou
 * nommé se voit, se compte et se remplit.
 *
 * POURQUOI UNE VALEUR DE `family`, ET PAS UNE CLEF DE PLUS. Une clef `aDecider`
 * à côté de `family` écrirait le même fait deux fois (le nom de la famille, et
 * le drapeau qui dit que ce n'en est pas un) : les deux divergeraient au premier
 * oubli. Ici, il n'y a qu'un endroit à lire.
 *
 * POURQUOI L'ID DE PORTE DEDANS. Les jeux sont dédupliqués sur `family::type` :
 * deux portes qui délèguent toutes deux leur famille fusionneraient en un seul
 * jeu et mélangeraient leurs champs. L'id de la porte est le seul discriminant
 * qui n'invente rien. Il va dans `family` et JAMAIS dans `type` : `type` part
 * dans Firestore sur chaque document, y écrire un id de porte survivrait à la
 * correction.
 *
 * POURQUOI UN `@`. Une famille du socle est un identifiant en minuscules
 * (`assets`, `contacts`) : aucune ne peut commencer par `@`, donc la confusion
 * est impossible et aucun fichier existant ne change de sens.
 *
 * CE MOT NE S'AFFICHE JAMAIS TEL QUEL : le Studio écrit « à décider ». Le lien
 * entre les deux fichiers de déclaration est cette VALEUR, jamais un libellé.
 */
export const FAMILLE_A_DECIDER = "@a-decider";

/** Le trou nommé d'une porte donnée : `@a-decider/<id de porte>`. */
export function familleADeciderDe(porteId: string): string {
  return `${FAMILLE_A_DECIDER}/${porteId}`;
}

/** Vrai quand cette famille est un trou, avec ou sans son id de porte. */
export function estFamilleADecider(family: string): boolean {
  return family === FAMILLE_A_DECIDER || family.startsWith(`${FAMILLE_A_DECIDER}/`);
}

/**
 * LES FAUTES D'UNE LECTURE contre les jeux du fichier, une phrase par faute
 * (chacune nomme le champ). Ne juge que ce que le fichier sait :
 * - `via[0]`, quand c'est une clef nue : un champ `lien` SANS `plusieurs` du
 *   MEME jeu (plusieurs fiches n'ont pas UNE valeur a lire) ;
 * - `via[1]`, quand c'est une clef nue et que le jeu qu'`via[0]` designe est
 *   declare ici : un `lien` sans `plusieurs` de ce jeu ;
 * - `champ`, quand c'est une clef nue et que le jeu atteint est declare ici :
 *   il existe, et n'est pas une `lecture` (pas de lecture de lecture).
 * Un natif (`socle:`) ou un jeu hors du fichier se juge ailleurs : au montage de
 * l'ecran (`resoudreLecture`, @taki/ui) et a la CI (`check-app-entities.mjs`).
 */
export function fautesDeLecture(
  jeux: readonly { family: string; type?: string | undefined; champs: readonly AppField[] }[],
  jeu: { family: string; champs: readonly AppField[] },
  champ: { key: string; lecture?: LectureDeChamp | undefined },
): string[] {
  const lecture = champ.lecture;
  if (lecture === undefined || lecture.via.length === 0 || lecture.via.length > ETAGES_MAX_D_UNE_LECTURE) return [];
  const de = `le champ "${champ.key}" (${jeu.family})`;
  const fautes: string[] = [];
  const nu = (nom: string): boolean => !nom.startsWith("socle:");
  /* Un etage : le champ `lien` nomme, dans le jeu dont on parle (jamais `plusieurs`). */
  const etage = (nom: string, dans: readonly AppField[], ou: string): AppField | null => {
    if (!nu(nom)) return null;
    const lien = dans.find((candidat) => candidat.key === nom);
    if (lien === undefined) {
      fautes.push(`${de} lit par "${nom}", qui n'est pas un champ de ${ou}`);
      return null;
    }
    if (lien.type !== "lien" || lien.plusieurs === true) {
      fautes.push(`${de} lit par "${nom}", qui n'est pas un champ lien vers UNE fiche (${lien.plusieurs === true ? "il en désigne plusieurs" : `type ${lien.type}`})`);
      return null;
    }
    return lien;
  };
  const jeuDe = (reference: { family: string; type?: string | undefined } | undefined) =>
    reference === undefined ? undefined : jeux.find((candidat) => candidat.family === reference.family && candidat.type === reference.type);
  let atteint: { family: string; champs: readonly AppField[] } | undefined;
  const premier = etage(lecture.via[0]!, jeu.champs, jeu.family);
  atteint = jeuDe(premier?.reference);
  if (lecture.via.length === 2) {
    /* Le second etage se juge sur le jeu atteint, s'il est declare ici. */
    const second = atteint === undefined ? null : etage(lecture.via[1]!, atteint.champs, atteint.family);
    atteint = jeuDe(second?.reference);
  }
  if (atteint !== undefined && nu(lecture.champ)) {
    const lu = atteint.champs.find((candidat) => candidat.key === lecture.champ);
    if (lu === undefined) fautes.push(`${de} lit "${lecture.champ}", qui n'est pas un champ de ${atteint.family}`);
    else if (lu.type === "lecture") fautes.push(`${de} lit "${lecture.champ}", qui est lui-même une lecture : une lecture ne lit pas une lecture`);
  }
  return fautes;
}

/**
 * UN JEU DE DONNÉES déclaré, et ses champs.
 *
 * L'entrée est par JEU, jamais par porte : deux portes qui affichent le même
 * jeu partagent ses champs, et les écrire deux fois serait deux faits qui
 * divergeront.
 */
export const appFieldSetSchema = z
  .object({
    /**
     * Famille du socle (`assets`, `contacts`...), recoupée avec la fiche, OU le
     * trou nommé `@a-decider/<id de porte>` quand elle reste à décider.
     */
    family: z.string().min(1),
    /** Type local qui spécialise la famille (`materiel`), s'il y en a un. */
    type: z.string().min(1).optional(),
    /** Portee de securite du jeu, independante de ses portes. */
    scope: z.enum(["org-wide", "owner-scoped"]).optional(),
    /** Nom du jeu, même quand aucune porte ne le montre encore. */
    nom: z.string().trim().min(1).optional(),
    /**
     * Les champs du jeu. LE VIDE EST ACCEPTÉ, et c'est ce que ce chantier a
     * cédé : une app naît avec ses deux fichiers de déclaration, donc un jeu
     * peut exister avant qu'un seul champ soit décidé. Un tableau vide se lit
     * « rien de personnalisé ici, pas encore » ; c'est la CI qui le COMPTE.
     */
    champs: z.array(appFieldSchema),
    /**
     * LES FICHIERS JOINTS, une capacité des données (`fichiers-jeu.ts`).
     * Absente : coupée, le serveur refuse tout ajout sur ce jeu.
     */
    fichiers: fichiersDeJeuSchema.optional(),
    /**
     * LE CHATTER SUR LES FICHES de ce jeu (vague du soir, R9). Absent : allume,
     * c'est le defaut. `false` : pas de bouton Chatter ni de bandeau, et le
     * serveur archive tout message neuf ecrit dans le chatter d'une fiche de
     * ce jeu ; les messages d'avant restent et reviennent quand on rallume.
     * Ecrit par le Studio (section du jeu), jamais `true` (le defaut ne
     * s'ecrit pas).
     */
    chatter: z.literal(false).optional(),
    /**
     * LA COULEUR DES FICHES de ce jeu (`docs/socle/couleur-photo-fiche.md`).
     * Absente : eteinte, c'est le defaut, rien ne change. `true` : chaque fiche
     * peut porter une couleur (`couleur`, champ standard), montree en anneau
     * autour de sa pastille. Ecrit par le Studio (section du jeu), jamais `false`.
     */
    couleur: z.literal(true).optional(),
    /**
     * LA PHOTO DE REFERENCE des fiches de ce jeu, meme regle : absente, eteinte ;
     * `true` : une photo par fiche (`photo`, champ standard), envoyee depuis la
     * fiche et rangee avec ses fichiers. Le serveur refuse l'envoi sans elle.
     */
    photo: z.literal(true).optional(),
    /**
     * LES CHAMPS MASQUES DU JEU, par clef (`docs/socle/jeu-listes.md`). Un champ
     * masque reste dans le jeu et dans les fiches deja ecrites, rien n'est
     * supprime ; mais aucune fiche ne le montre, dans aucune app, et Parametres
     * n'en regle pas la liste. C'est un reglage du JEU, donc de toutes les apps
     * qui le montrent : une org masque chez elle ce que le modele declare.
     * Absent : rien n'est masque. Ecrit par le Studio (section du jeu).
     */
    masques: z.array(z.string().regex(/^[a-z][a-zA-Z0-9_]*$/)).max(200).optional(),
  })
  .superRefine((jeu, ctx) => {
    for (const clef of jeu.masques ?? []) {
      const champ = jeu.champs.find((candidat) => candidat.key === clef);
      if (champ === undefined) ctx.addIssue({ code: "custom", message: `le champ masque "${clef}" n'est pas un champ de ${jeu.family}`, path: ["masques"] });
      else if (champ.required === true) ctx.addIssue({ code: "custom", message: `le champ "${clef}" est obligatoire : il ne peut pas etre masque`, path: ["masques"] });
    }
    if (estFamilleADecider(jeu.family) && jeu.scope !== undefined) ctx.addIssue({ code: "custom", message: "une portee exige une famille", path: ["scope"] });
    /*
     * UN TYPE LOCAL SPÉCIALISE UNE FAMILLE. Sans famille, il ne veut rien dire :
     * il partirait dans Firestore comme `type` des documents d'un jeu dont
     * personne ne sait encore où il vit. On le refuse tant que le trou est là.
     */
    if (estFamilleADecider(jeu.family) && jeu.type !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `le jeu "${jeu.family}" n'a pas de famille : un type local n'y veut rien dire`,
      });
    }
  });

/** Un jeu de données déclaré. */
export type AppFieldSet = z.infer<typeof appFieldSetSchema>;

/**
 * LE FICHIER `taki.fields.json` d'une app, en entier.
 *
 * `version` est celle de la FORME du fichier, jamais celle de l'app : elle
 * change quand ce schéma change, pas quand l'app livre.
 */
export const appFieldsFileSchema = z
  .object({
    version: z.literal(1),
    jeux: z.array(appFieldSetSchema).min(1),
  })
  .superRefine((fichier, ctx) => {
    for (const jeu of fichier.jeux) {
      const vues = new Set<string>();
      for (const champ of jeu.champs) {
        if (vues.has(champ.key)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `la clé "${champ.key}" est déclarée deux fois dans ${jeu.family}`,
          });
        }
        vues.add(champ.key);
      }
      for (const faute of fautesDeDependance(jeu.champs)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${jeu.family} : ${faute}` });
      }
    }
    /*
     * LES FORMULES SE JUGENT CONTRE LEUR JEU : un champ cite qui n'en est pas
     * un, un champ d'une autre nature qu'un nombre, une boucle entre calculs.
     * C'est ce controle qui refuse une formule cassee au Studio comme a la CI,
     * et qui refuse la declaration le jour ou un champ cite disparait du jeu.
     * Une formule illisible est deja refusee sur son champ : on ne la redit pas.
     * UN NATIF NUMERIQUE DE LA FAMILLE se cite aussi (`{socle:basePrice}`) : il
     * doit exister dans la famille du jeu et etre un nombre.
     */
    for (const [rang, jeu] of fichier.jeux.entries()) {
      for (const [place, champ] of jeu.champs.entries()) {
        if (champ.type !== "calcul" || champ.formule === undefined || "erreur" in analyserFormule(champ.formule)) continue;
        for (const faute of verifierFormule(champ.formule, champ, champsAvecNatifs(jeu.family, jeu.champs))) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${jeu.family} : ${faute}`, path: ["jeux", rang, "champs", place, "formule"] });
        }
      }
    }
    /*
     * LES LECTURES SE JUGENT CONTRE LE FICHIER (`fautesDeLecture`) : le premier
     * lien est un `lien` simple du meme jeu, et un champ lu d'un jeu declare ici
     * existe et n'est pas lui-meme une lecture. Ce qu'on ne peut pas voir ici
     * (un natif, un jeu hors du fichier) se juge au montage de l'ecran et a la CI.
     */
    for (const [rang, jeu] of fichier.jeux.entries()) {
      for (const [place, champ] of jeu.champs.entries()) {
        if (champ.type !== "lecture" || champ.lecture === undefined) continue;
        for (const faute of fautesDeLecture(fichier.jeux, jeu, champ)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: faute, path: ["jeux", rang, "champs", place, "lecture"] });
        }
      }
    }
  });

/** Le contenu d'un `taki.fields.json`. */
export type AppFieldsFile = z.infer<typeof appFieldsFileSchema>;

/**
 * LIRE UN `taki.fields.json`, et dire POURQUOI quand il est faux.
 *
 * Pourquoi cette fonction existe : un `import` de JSON n'est PAS vérifié par
 * TypeScript. Il élargit toute union littérale en `string`, donc
 * `"type": "slect"` compile en vert et ne se voit nulle part. Ce `safeParse` est
 * la compensation exacte de ce trou, et il jette avec le chemin du champ fautif
 * plutôt que de rendre un écran à moitié construit.
 *
 * En CI, le même fichier est vérifié par `scripts/check-app-entities.mjs`, et
 * c'est LUI qui fait foi : ici on protège le poste de développement, là-bas on
 * protège `main`.
 */
export function lireChampsDeclares(brut: unknown, source: string): AppFieldsFile {
  // Une portee restreinte publiee avant la regle sur le fil d'echanges se relit
  // heritee (`sansPorteeInterdite`, portee-jeu.mjs) : l'app monte, la
  // publication suivante ne la porte plus. Un type fondu le 27/10/2026
  // (`montant`, `pourcentage`, `duree`, `liens`) se relit de meme dans sa forme
  // nouvelle (`sansTypeFondu`, catalogue-des-types.mjs).
  const lu = appFieldsFileSchema.safeParse(sansTypeFondu(sansPorteeInterdite(brut)));
  if (lu.success) return lu.data;
  const details = lu.error.issues
    .map((issue) => `${issue.path.join(".") || "(racine)"} : ${issue.message}`)
    .join(" | ");
  throw new Error(`Fichier de champs invalide (${source}) : ${details}`);
}
